import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import FifaVerifiedBadge, { isFifaVerified } from "./FifaVerifiedBadge";

// Helper to calculate age
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "-";
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const AgentHeroBanner = ({
  agent,
  className = "",
  isOnClickNavigate = false,
}) => {
  const navigate = useNavigate();
  const [flagError, setFlagError] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Calculate age if date_of_birth is available
  const agentAge = agent?.date_of_birth
    ? calculateAge(agent.date_of_birth)
    : null;

  // DECISION-042 (Marin, 2026-07-29): "kein Label. Auslassung bleibt." An agent
  // without an agency carries no agency wording at all — not "Independent", and
  // above all not the "-" this banner used to paint. The whole agency card is
  // dropped so no orphaned "Agency:" caption is left behind.
  //
  // The same rule reaches the other two cards:
  //   * License:   the AGENT's own raw licence number
  //     (details.license_number — the same field as the search table's
  //     "License No." column). Marin (D-A QA, 2026-08-04): the card prints
  //     the number and NOTHING else — no "Verified", no "Self-declared";
  //     the FIFA Verified badge next to the name (fed by
  //     details.license_verification_status / AgentLicenseVerification) is
  //     the ONLY verified statement on this banner. No number, no card.
  //   * TP Points: agent.agency.total_transfer_points → the AGENCY wallet
  //     balance (AgencyListSerializer.get_total_transfer_points).
  // With no agency there is no subject for either claim, so both cards are
  // dropped whole — dropping only the values would leave "License:" and
  // "TP Points:" captions standing on their own, the exact orphan the agency
  // card was dropped to avoid.
  const hasAgency = Boolean(agent?.agency?.name);

  // D-A: the AGENT's FIFA Verified badge, next to the name per the
  // approved board — solid variant because the banner is a dark
  // gradient. Badge rule (Marin 2026-08-04): the agent's OWN
  // self-declared licence number OR fifa_verified carries it; only
  // failed_qr hides it (isFifaVerified). Never from the agency's
  // self-declared has_fifa_license.
  const fifaVerified = isFifaVerified(agent);

  // Mobile Layout
  if (isMobile) {
    return (
      <div
        onClick={() => {
          if (isOnClickNavigate) {
            navigate(`/search/agent/${agent?.id}`);
          }
        }}
        className={`bg-gradient-to-r from-[#1A212F] to-[#374DF5] rounded-xl text-white relative overflow-hidden cursor-pointer ${className}`}
      >
        {/* Mobile Content */}
        <div className="w-full p-4 relative z-10">
          {/* Top Section - Image and Name. Carries the bottom margin only when
              an info-card block follows it. */}
          <div
            className={`flex gap-3 items-center ${hasAgency ? "mb-4" : ""}`}
          >
            {/* Agent Image */}
            <div className="w-[60px] h-[60px] rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
              {agent?.profile_picture_url ? (
                <img
                  src={agent.profile_picture_url}
                  alt={agent.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-600 flex items-center justify-center">
                  <span className="text-gray-400 text-xl font-bold">
                    {agent?.name?.charAt(0) || "A"}
                  </span>
                </div>
              )}
            </div>

            {/* Agent Name (+ FIFA Verified badge, board: H 32 next to the
                name, wrapping below it when 375px runs out of room) */}
            <div className="flex flex-col justify-center flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <h1 className="text-xl font-bold text-white">
                  {agent?.name || "-"}
                </h1>
                {fifaVerified && <FifaVerifiedBadge size="lg" solid />}
              </div>
            </div>

            {/* Country Flag - Top Right */}
            <div className="flex items-center">
              {agent?.country && !flagError ? (
                <img
                  src={`https://flagcdn.com/${agent.country
                    .toLowerCase()
                    .substring(0, 2)}.svg`}
                  alt={agent.country}
                  className="h-5 w-7 rounded-sm"
                  onError={() => setFlagError(true)}
                />
              ) : (
                <div className="h-5 w-7 bg-gradient-to-r from-blue-500 via-white to-red-500 rounded-sm flex items-center justify-center">
                  <span className="text-[10px] font-bold text-black">
                    {agent?.country?.substring(0, 2).toUpperCase() || "NA"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Section - Info Cards. Every card in it is an AGENCY fact
              (Agency / License / TP Points — see hasAgency above), so for an
              agent without an agency the whole block is omitted rather than
              painted with dashes and an unowned "Not Verified". */}
          {hasAgency && (
          <div className="flex flex-col gap-2">
            {/* Agency Card (DECISION-042: absence renders nothing, no label and
                no dash — the guard sits on the block above so no orphaned
                "Agency:" caption is ever left behind). */}
            <div className="bg-white backdrop-blur-sm rounded-xl px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#1A212F] whitespace-nowrap">Agency:</span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {agent?.agency?.profile_picture_url ? (
                    <img
                      src={agent.agency.profile_picture_url}
                      alt=""
                      className="w-5 h-5 object-contain rounded-full flex-shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-[10px] font-bold">
                        {agent.agency.name.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-xs font-bold text-[#1A212F] truncate">
                    {agent.agency.name}
                  </span>
                </div>
              </div>
            </div>

            {/* License and TP Points Row. Marin (D-A QA, 2026-08-04): the
                License card prints the agent's raw licence NUMBER — same
                field as the search table's "License No." column — with no
                wording around it ("Self-declared"/"Verified" both gone; the
                FIFA badge next to the name is the ONLY verified statement).
                No number -> the card is omitted entirely (DECISION-042
                absence-renders-nothing rule). */}
            <div className="flex gap-2">
              {agent?.license_number && (
                <div className="bg-white backdrop-blur-sm rounded-xl px-3 py-2 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#1A212F] whitespace-nowrap">License:</span>
                    <span className="text-xs font-bold text-[#1A212F] truncate" data-no-translate>
                      {agent.license_number}
                    </span>
                  </div>
                </div>
              )}

              {/* TP Points Card */}
              <div className="bg-white backdrop-blur-sm rounded-xl px-3 py-2 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#1A212F] whitespace-nowrap">TP Points:</span>
                  <span className="text-xs font-bold text-[#1A212F]">
                    {agent?.agency?.total_transfer_points !== undefined &&
                    agent?.agency?.total_transfer_points !== null
                      ? agent.agency.total_transfer_points
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop Layout (unchanged)
  return (
    <div
      onClick={() => {
        if (isOnClickNavigate) {
          navigate(`/search/agent/${agent?.id}`);
        }
      }}
      className={`bg-gradient-to-r from-[#1A212F] to-[#374DF5] flex items-center rounded-xl text-white relative overflow-hidden h-[200px] cursor-pointer ${className}`}
    >
      {/* Main Content */}
      <div className="w-full mx-auto px-14 py-8 flex items-center relative z-10">
        <div className="flex gap-8 items-center w-full justify-between">
          {/* Left Section - Image and Basic Info */}
          <div className="flex gap-6">
            {/* Agent Image */}
            <div className="w-[100px] h-[100px] rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center">
              {agent?.profile_picture_url ? (
                <img
                  src={agent.profile_picture_url}
                  alt={agent.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-600 flex items-center justify-center">
                  <span className="text-gray-400 text-2xl font-bold">
                    {agent?.name?.charAt(0) || "A"}
                  </span>
                </div>
              )}
            </div>

            {/* Basic Info (+ FIFA Verified badge H 32, board 1d: next to
                the name, solid on the dark banner) */}
            <div className="flex flex-col justify-center">
              <div className="flex items-center flex-wrap gap-3 mb-3">
                <h1 className="text-4xl font-bold text-white">
                  {agent?.name || "-"}
                </h1>
                {fifaVerified && <FifaVerifiedBadge size="lg" solid />}
              </div>
            </div>
          </div>

          {/* Right Section - Key Details Cards. Agency / License / TP Points
              are all AGENCY facts (see hasAgency above), so the whole column is
              omitted for an agent without an agency — no dash, and no
              "Not Verified" about an agency that does not exist. */}
          {hasAgency && (
          <div className="flex flex-col gap-4 ml-auto">
            {/* Agency Card (DECISION-042: absence renders nothing, no label and
                no dash — the guard sits on the column above). */}
            <div className="flex items-center gap-4">
              <div className="bg-white backdrop-blur-sm rounded-xl px-4 py-2 min-w-[140px]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1A212F]">Agency:</span>
                  <div className="flex items-center gap-2">
                    {agent?.agency?.profile_picture_url ? (
                      <img
                        src={agent.agency.profile_picture_url}
                        alt=""
                        className="w-6 h-6 object-contain rounded-full"
                      />
                    ) : (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {agent.agency.name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-bold text-[#1A212F]">
                      {agent.agency.name}
                    </span>
                  </div>
                </div>
              </div>

              {/* License Card. Marin (D-A QA, 2026-08-04): raw licence
                  number only — no "Self-declared"/"Verified" wording (the
                  FIFA badge next to the name is the only verified
                  statement); no number omits the card. */}
              {agent?.license_number && (
                <div className="bg-white backdrop-blur-sm rounded-xl px-4 py-2 min-w-[140px]">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#1A212F]">License:</span>
                    <span className="text-sm font-bold text-[#1A212F]" data-no-translate>
                      {agent.license_number}
                    </span>
                  </div>
                </div>
              )}

              {/* TP Points Card */}
              <div className="bg-white backdrop-blur-sm rounded-xl px-4 py-2 min-w-[140px]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1A212F]">TP Points:</span>
                  <span className="text-sm font-bold text-[#1A212F]">
                    {agent?.agency?.total_transfer_points !== undefined &&
                    agent?.agency?.total_transfer_points !== null
                      ? agent.agency.total_transfer_points
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Country Flag - Bottom Right */}
      <div className="absolute right-6 bottom-6 flex items-center gap-3">
        {agent?.country && !flagError ? (
          <img
            src={`https://flagcdn.com/${agent.country
              .toLowerCase()
              .substring(0, 2)}.svg`}
            alt={agent.country}
            className="h-6 w-8 rounded-sm"
            onError={() => setFlagError(true)}
          />
        ) : (
          <div className="h-6 w-8 bg-gradient-to-r from-blue-500 via-white to-red-500 rounded-sm flex items-center justify-center">
            <span className="text-xs font-bold text-black">
              {agent?.country?.substring(0, 2).toUpperCase() || "NA"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentHeroBanner;
